import React from 'react';
import Toggle from '../toggle';
import SearchField from 'components/general/searchField';
import { Paging, SystemText, Loading } from '@salesperformanceint/e4shared';
import Bullet from '../bullet';
import { useSelector } from 'react-redux';
import { apiGet } from 'services';
import { e4Format, isPast, isToday } from 'helpers/dates';
import { Link } from 'react-router-dom';
import NoResults from '../noResults';
import ManageTask from 'views/user/components/manageTask';
import TaskCalendar from 'components/tasks/taskCalendar';

export const ObjectiveList = ({ profileId, onStatusChange, onDataChange, displayDetails }) => {
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [status, setStatus] = React.useState('outstanding-awaiting');
    const [search, setSearch] = React.useState('');
    const [tempSearch, setTempSearch] = React.useState('');
    const [view, setView] = React.useState('LIST');
    const [page, setPage] = React.useState(1);
    const [showAddTask, setShowAddTask] = React.useState(false);

    const { user } = useSelector(state => state.user);

    const pageSize = 10;

    const text = {
        status: {
            outstanding: SystemText('ObjectiveList/Statuses/Outstanding'),
            completed: SystemText('ObjectiveList/Statuses/Completed'),
        },
        search: {
            placeholder: SystemText('ObjectiveList/Search/Placeholder'),
            filter: SystemText('ObjectiveList/Search/Filter'),
        },
        add: SystemText('ObjectiveList/AddNew'),
        noResults: {
            outstanding: SystemText('ObjectiveList/NoResults/Outstanding/Title'),
            outstandingSummary: SystemText('ObjectiveList/NoResults/Outstanding/Summary'),
            completed: SystemText('ObjectiveList/NoResults/Completed/Title'),
            completedSummary: SystemText('ObjectiveList/NoResults/Completed/Summary'),
            search: SystemText('ObjectiveList/NoResults/Search/Title'),
            searchSummary: SystemText('ObjectiveList/NoResults/Search/Summary'),
        },
        table: {
            objective: SystemText('ObjectiveList/Table/Objective'),
            type: SystemText('ObjectiveList/Table/Type'),
            assigned: SystemText('ObjectiveList/Table/Assigned'),
            assignedBy: SystemText('ObjectiveList/Table/AssignedBy'),
            due: SystemText('ObjectiveList/Table/DueDate'),
            status: SystemText('ObjectiveList/Table/Status'),
            completed: SystemText('ObjectiveList/Table/Completed'),
        },
    };

    React.useEffect(() => {
        setPage(1);
    }, [profileId, status, search]);

    const doSearch = () => {
        if (profileId) {
            setLoading(true);
            apiGet(
                `profile/${profileId}/tasks?page=${page}&filter=${status}&search=${search.trim()}&sort=awaiting-approval`,
                user.guid,
                response => {
                    setData(response);
                    setLoading(false);
                    // Notify parent component of data change
                    if (onDataChange) {
                        onDataChange(response);
                    }
                }
            );
        }
    };

    React.useEffect(() => {
        doSearch();
    }, [profileId, page, status, search]);

    // Notify parent component when status changes
    React.useEffect(() => {
        if (onStatusChange) {
            onStatusChange(status);
        }
    }, [status]);

    const taskStatus = task => {
        if (task.status === 'Outstanding') {
            if (isToday(task.due)) {
                return 'Due_Today';
            }

            if (isPast(task.due)) {
                return 'Overdue';
            }
        }

        return task.status;
    };

    if (loading) {
        return <Loading />;
    }

    return (
        <>
            <div className="mb10">
                <Toggle value={status} onChange={setStatus}>
                    <button value="outstanding-awaiting">{text.status.outstanding}</button>
                    <button value="completed">{text.status.completed}</button>
                </Toggle>
            </div>
            <div className="mb20 prism-filter">
                <div className="flex flex-center">
                    <SearchField
                        onSubmit={() => setSearch(tempSearch)}
                        value={tempSearch}
                        onChange={setTempSearch}
                        placeholder={text.search.placeholder}
                    />
                    <button className="btn btn-filter" onClick={() => setSearch(tempSearch)}>
                        {text.search.filter}
                    </button>
                </div>
                <div className="text-right">
                    <Toggle value={view} onChange={setView}>
                        <button value="LIST">
                            <i className="fa fa-list" />
                        </button>
                        <button value="CAL">
                            <i className="fa fa-calendar" />
                        </button>
                    </Toggle>
                    <button className="ml10 btn btn-primary" onClick={() => setShowAddTask(true)}>
                        {text.add}
                        <i className="ml10 fa fa-plus" />
                    </button>
                </div>
            </div>
            <div className="text-center">
                {view === 'LIST' ? (
                    data.tasks.length > 0 ? (
                        <>
                            <table className="prism-table mb10">
                                <thead>
                                    <tr>
                                        <th>{text.table.objective}</th>
                                        <th>{text.table.type}</th>
                                        <th>{text.table.assigned}</th>
                                        <th>{text.table.assignedBy}</th>
                                        <th>{text.table.due}</th>
                                        <th>{text.table.status}</th>
                                        {status === 'completed' && <th>{text.table.completed}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.tasks.map(d => {
                                        return (
                                            <tr key={`objective-${d.id}`}>
                                                <td>
                                                    {d.pathway_Id && <span className="mr10">-</span>}

                                                    {displayDetails ? (
                                                        <>
                                                            <a
                                                                href="#"
                                                                onClick={e => {
                                                                    e.preventDefault();
                                                                    displayDetails(d.id);
                                                                }}
                                                            >
                                                                {d.name}
                                                            </a>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Link to={`/objectives/${profileId}/objective/${d.id}`}>
                                                                {d.name}
                                                            </Link>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    <Bullet type="objective" subType={d.taskType} />
                                                </td>
                                                <td>{e4Format(d.date_Assigned)}</td>
                                                <td>{d.owner_Fullname || '-'}</td>
                                                <td>
                                                    <Bullet isDate value={d.due} />
                                                </td>
                                                <td>
                                                    {d.fully_Completed ? (
                                                        <Bullet isStatus value={d.completed_Status} />
                                                    ) : (
                                                        <Bullet isStatus value={taskStatus(d)} />
                                                    )}
                                                </td>
                                                {status === 'completed' && <td>{e4Format(d.date_Completed)}</td>}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            <Paging page={page} pageSize={pageSize} recordCount={data.records} goToPage={setPage} />
                        </>
                    ) : (
                        <div style={{ marginTop: '100px' }}>
                            {!search ? (
                                status === 'completed' ? (
                                    <NoResults
                                        icon="square-check"
                                        title={text.noResults.completed}
                                        summary={text.noResults.completedSummary}
                                    />
                                ) : (
                                    <NoResults
                                        icon="square-check"
                                        title={text.noResults.outstanding}
                                        summary={text.noResults.outstandingSummary}
                                    />
                                )
                            ) : (
                                <NoResults
                                    icon="square-check"
                                    title={text.noResults.search}
                                    summary={text.noResults.searchSummary}
                                />
                            )}
                        </div>
                    )
                ) : (
                    <div className="mt20">
                        <TaskCalendar profileId={profileId} filter={status} search={search} />
                    </div>
                )}
            </div>
            {showAddTask && (
                <ManageTask
                    key={`profile${profileId}`}
                    profileId={profileId}
                    values={{ profile_Ids: user?.support && user?.id === profileId ? [] : [profileId] }}
                    show={showAddTask}
                    close={() => {
                        doSearch();
                        setShowAddTask(false);
                    }}
                />
            )}
        </>
    );
};

export default ObjectiveList;
